<!-- Nút + popover "Tỷ giá & giá vàng" — đối chiếu FxButton (module-rail.jsx). Khác biệt có chủ
     đích với bản thiết kế ghi ở doc-comment của component. -->
<ds-button
  variant="ghost"
  size="sm"
  [iconOnly]="true"
  icon="paid"
  [ariaLabel]="'Layout.Fx.Title' | translate"
  [dsTooltip]="'Layout.Fx.Title' | translate"
  [dsPopover]="panel"
  dsPopoverPlacement="bottom"
  (dsPopoverOpened)="onOpened($event)"
/>

<ng-template #panel>
  <div
    class="w-[92vw] max-w-md bg-warm-card border border-warm-border rounded-lg shadow-lg overflow-hidden"
    data-testid="fx-panel"
  >
    <div class="flex items-baseline gap-2 px-4 pt-3">
      <span class="text-token-body font-semibold text-warm-text">
        {{ 'Layout.Fx.Title' | translate }}
      </span>
      <span class="flex-1"></span>
      @if (board.quotedAt) {
        <span
          class="text-token-caption tabular-nums"
          [class.text-warm-hint]="!isStale"
          [class.text-amber-600]="isStale"
          [dsTooltip]="isStale ? ('Layout.Fx.Stale' | translate) : ''"
          data-testid="fx-source"
        >
          {{ 'Layout.Fx.Source' | translate: { time: quotedAtLabel } }}
        </span>
      }
    </div>

    <ds-tabs class="block px-2 mt-1" [tabs]="tabs" [activeTab]="activeTab" (tabChange)="onTabChange($event)" />

    @if (loading && !board.rates.length) {
      <div class="px-4 py-8 text-center text-token-body-dense text-warm-hint">
        {{ 'Layout.Fx.Loading' | translate }}
      </div>
    } @else if (loadError) {
      <div class="px-4 py-8 text-center" data-testid="fx-error">
        <div class="text-token-body-dense text-warm-text">{{ 'Layout.Fx.LoadError' | translate }}</div>
        <ds-button class="mt-3 inline-block" variant="secondary" size="sm" (click)="load()">
          {{ 'Layout.Fx.Retry' | translate }}
        </ds-button>
      </div>
    } @else {
      @switch (activeTab) {
        @case ('rates') {
          @if (board.rates.length) {
            <div class="max-h-96 overflow-y-auto">
              <table class="w-full border-collapse" data-testid="fx-rates-table">
                <thead>
                  <tr class="bg-warm-table-header text-token-caption text-warm-secondary">
                    <th class="text-left font-semibold px-3 py-2 border-b border-warm-border">
                      {{ 'Layout.Fx.Col.Currency' | translate }}
                    </th>
                    <th class="text-right font-semibold px-3 py-2 border-b border-warm-border">
                      {{ 'Layout.Fx.Col.Bid' | translate }}
                    </th>
                    <th class="text-right font-semibold px-3 py-2 border-b border-warm-border">
                      {{ 'Layout.Fx.Col.Ask' | translate }}
                    </th>
                    <th class="text-right font-semibold px-3 py-2 border-b border-warm-border">
                      {{ 'Layout.Fx.Col.Mid' | translate }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (r of board.rates; track r.code) {
                    <tr
                      class="cursor-pointer hover:bg-warm-hover text-token-body-dense text-warm-text tabular-nums"
                      tabindex="0"
                      (click)="pickRate(r.code)"
                      (keydown.enter)="pickRate(r.code)"
                    >
                      <td class="px-3 py-2 border-b border-warm-border-light">
                        <span class="font-semibold">{{ r.code }}</span>
                        <span class="ml-1 text-token-caption text-warm-hint">{{ currencyName(r.code) }}</span>
                      </td>
                      <td class="px-3 py-2 text-right border-b border-warm-border-light">{{ fmt(r.bid) }}</td>
                      <td class="px-3 py-2 text-right border-b border-warm-border-light">{{ fmt(r.ask) }}</td>
                      <td class="px-3 py-2 text-right font-semibold border-b border-warm-border-light">
                        {{ fmt(r.mid) }}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
              <div class="px-4 py-2 text-token-caption text-warm-hint">{{ 'Layout.Fx.RatesHint' | translate }}</div>
            </div>
          } @else {
            <div class="px-4 py-8 text-center" data-testid="fx-empty">
              <div class="text-token-body-dense text-warm-text">{{ 'Layout.Fx.Empty' | translate }}</div>
              <div class="mt-1 text-token-caption text-warm-hint">{{ 'Layout.Fx.EmptyHint' | translate }}</div>
            </div>
          }
        }

        @case ('convert') {
          @if (codeOptions.length) {
            <div class="p-4" data-testid="fx-convert">
              <div class="flex items-end gap-2">
                <ds-number-input
                  class="flex-1 min-w-0"
                  [label]="'Layout.Fx.Amount' | translate"
                  [decimals]="2"
                  [(ngModel)]="amount"
                />
                <ds-select
                  class="w-20 shrink-0"
                  [label]="'Layout.Fx.From' | translate"
                  [options]="codeOptions"
                  [value]="from"
                  (valueChange)="setFrom($event)"
                />
                <ds-button
                  variant="secondary"
                  size="md"
                  [iconOnly]="true"
                  icon="swap_horiz"
                  [ariaLabel]="'Layout.Fx.Swap' | translate"
                  [dsTooltip]="'Layout.Fx.Swap' | translate"
                  (click)="swap()"
                />
                <ds-select
                  class="w-20 shrink-0"
                  [label]="'Layout.Fx.To' | translate"
                  [options]="codeOptions"
                  [value]="to"
                  (valueChange)="setTo($event)"
                />
              </div>

              <div class="mt-3 px-3 py-3 rounded-md bg-warm-surface tabular-nums" data-testid="fx-result">
                <div class="text-token-caption text-warm-secondary">{{ fmt(amount ?? 0, 2) }} {{ from }} =</div>
                <div class="mt-0.5 text-token-heading-sm font-semibold text-warm-text">
                  {{ fmt(result) }}
                  <span class="text-token-body-dense font-medium text-warm-secondary">{{ to }}</span>
                </div>
                @if (rate !== null) {
                  <div class="mt-1 text-token-caption text-warm-hint">
                    {{ 'Layout.Fx.ResultNote' | translate: { from: from, to: to, rate: fmt(rate) } }}
                  </div>
                }
              </div>

              @if (commonPairs.length) {
                <div class="mt-3 mb-1.5 text-token-label font-semibold text-warm-hint">
                  {{ 'Layout.Fx.CommonPairs' | translate }}
                </div>
                <div class="grid grid-cols-3 gap-1.5">
                  @for (p of commonPairs; track p.from + p.to) {
                    <button
                      type="button"
                      class="text-left px-2.5 py-1.5 rounded-md border transition-colors cursor-pointer hover:bg-warm-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      [class.border-primary]="p.from === from && p.to === to"
                      [class.bg-selected-bg]="p.from === from && p.to === to"
                      [class.border-warm-border]="!(p.from === from && p.to === to)"
                      (click)="pickPair(p.from, p.to)"
                    >
                      <div class="text-token-caption font-semibold text-warm-text">{{ p.from }}/{{ p.to }}</div>
                      <div class="text-token-caption text-warm-secondary tabular-nums">{{ fmt(p.rate) }}</div>
                    </button>
                  }
                </div>
              }
            </div>
          } @else {
            <div class="px-4 py-8 text-center">
              <div class="text-token-body-dense text-warm-text">{{ 'Layout.Fx.Empty' | translate }}</div>
              <div class="mt-1 text-token-caption text-warm-hint">{{ 'Layout.Fx.EmptyHint' | translate }}</div>
            </div>
          }
        }

        @case ('gold') {
          @if (board.metals.length) {
            <table class="w-full border-collapse" data-testid="fx-gold-table">
              <thead>
                <tr class="bg-warm-table-header text-token-caption text-warm-secondary">
                  <th class="text-left font-semibold px-3 py-2 border-b border-warm-border">
                    {{ 'Layout.Fx.Col.Metal' | translate }}
                  </th>
                  <th class="text-right font-semibold px-3 py-2 border-b border-warm-border">
                    {{ 'Layout.Fx.Col.UsdPerOunce' | translate }}
                  </th>
                  <th class="text-right font-semibold px-3 py-2 border-b border-warm-border">
                    {{ 'Layout.Fx.Col.VndPerLuong' | translate }}
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (m of board.metals; track m.symbolCode) {
                  <tr class="text-token-body-dense text-warm-text tabular-nums">
                    <td class="px-3 py-2 border-b border-warm-border-light">
                      <div class="font-semibold">{{ 'Layout.Fx.Metal.' + m.symbolCode | translate }}</div>
                      <div class="text-token-caption text-warm-hint">{{ m.symbolCode }}</div>
                    </td>
                    <td class="px-3 py-2 text-right border-b border-warm-border-light">
                      {{ fmt(m.usdPerOunce.mid, 2) }}
                    </td>
                    <td class="px-3 py-2 text-right font-semibold border-b border-warm-border-light">
                      {{ fmt(m.vndPerLuong, 0) }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
            <div class="px-4 py-2 text-token-caption text-warm-hint">{{ 'Layout.Fx.GoldNote' | translate }}</div>
          } @else {
            <div class="px-4 py-8 text-center" data-testid="fx-gold-empty">
              <div class="text-token-body-dense text-warm-text">{{ 'Layout.Fx.GoldEmpty' | translate }}</div>
              <div class="mt-1 text-token-caption text-warm-hint">{{ 'Layout.Fx.GoldEmptyHint' | translate }}</div>
            </div>
          }
        }
      }
    }
  </div>
</ng-template>
